import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LOGO_URL, FIRM } from "../lib/firm";
import { ArrowClockwise } from "@phosphor-icons/react";
import {
  Stat,
  ABTestPanel,
  EventBreakdown,
  LeadsTable,
  LeadRow,
  InquiryRow,
} from "../components/site/AnalyticsParts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

async function fetchAnalytics() {
  const [s, l, c] = await Promise.all([
    axios.get(`${API}/events/summary`),
    axios.get(`${API}/lead-magnet/leads?limit=50`),
    axios.get(`${API}/contact?limit=50`),
  ]);
  return { summary: s.data, leads: l.data || [], inquiries: c.data || [] };
}

function AdminHeader({ onRefresh, loading }) {
  return (
    <header className="border-b border-sage-soft bg-cream">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
          <img
            src={LOGO_URL}
            alt={FIRM.name}
            className="h-16 lg:h-20 w-auto mix-blend-multiply"
          />
        </a>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline-block text-xs tracking-overline uppercase text-sage font-semibold">
            Internal &middot; Analytics Dashboard
          </span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-overline uppercase font-medium bg-deep-teal text-cream hover:bg-[var(--deep-teal-hover)] disabled:opacity-50 transition-colors"
          >
            <ArrowClockwise
              size={14}
              weight="light"
              className={loading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>
      </div>
    </header>
  );
}

function PageHeading() {
  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <span className="section-stamp text-xl">★</span>
        <span className="h-px w-10 bg-sage" />
        <span className="text-xs tracking-overline uppercase font-semibold text-sage">
          Live Snapshot
        </span>
      </div>
      <h1
        className="font-serif font-light text-deep-teal leading-[1.05] mb-12"
        style={{ fontSize: "clamp(2.25rem, 4vw, 3.25rem)" }}
      >
        Site performance &amp; <span className="italic">conversion</span>.
      </h1>
    </>
  );
}

function SummaryGrid({ summary, leadCount, inquiryCount }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
      <Stat label="Total Events" value={summary?.total_events ?? "—"} hint="All tracked interactions" />
      <Stat label="Unique Sessions" value={summary?.unique_sessions ?? "—"} hint="Distinct visitors" />
      <Stat label="Checklist Leads" value={leadCount} hint="PDF requests captured" />
      <Stat label="Contact Inquiries" value={inquiryCount} hint="Direct contact submissions" />
    </div>
  );
}

export default function AnalyticsAdmin() {
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchAnalytics();
      setSummary(data.summary);
      setLeads(data.leads);
      setInquiries(data.inquiries);
    } catch (e) {
      setErr(e?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <main className="bg-cream min-h-screen text-charcoal" data-testid="admin-analytics-page">
      <AdminHeader onRefresh={load} loading={loading} />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        {err && (
          <div
            className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 mb-8 text-sm"
            data-testid="admin-error"
          >
            {err}
          </div>
        )}

        <PageHeading />
        <SummaryGrid
          summary={summary}
          leadCount={leads.length}
          inquiryCount={inquiries.length}
        />
        <ABTestPanel ab={summary?.ab_test_checklist} />
        <EventBreakdown summary={summary} />

        <section className="grid lg:grid-cols-2 gap-12 mb-16">
          <LeadsTable
            title="Recent Checklist Leads"
            items={leads}
            testid="admin-leads-list"
            emptyText="No leads captured yet."
            RowComponent={LeadRow}
          />
          <LeadsTable
            title="Recent Contact Inquiries"
            items={inquiries}
            testid="admin-inquiries-list"
            emptyText="No inquiries yet."
            RowComponent={InquiryRow}
          />
        </section>

        <p className="text-xs text-charcoal-teal/60 italic">
          Internal page. Bookmark <code>/admin/analytics</code> — protect with
          access controls before public launch.
        </p>
      </div>
    </main>
  );
}
